import { isStateTreeNode, types, type SnapshotIn } from 'mobx-state-tree';
import type { Draft } from 'immer';
import ObjectType, { type IObjectType } from './object_type';
import EventType, { type IEventType } from './event_type';
import Dependency, { type IDependency } from './dependency';
import Method, { type IMethod } from './method';
import Inheritance, { type IInheritance } from './inheritance';
import DataType, { BUILTIN_DATA_TYPES, type IDataType } from './data_type';
import type { IFSM } from './fsm';
import type { IState } from './state';
import type { ITransition } from './transition';
import type { IMultiplePropagationConstraint } from './multiple_propagation_constraint';
import { flatten } from 'lodash';
import { produce } from 'immer';
import { values } from 'mobx';

/**
 * The MetaModel instance interface.
 *
 * This is HAND-DECLARED rather than `Instance<typeof MetaModel>` on purpose: it
 * is the anchor that breaks the mutual recursion of the whole cluster. Every
 * element model's `get metamodel()` is annotated with this interface, so TS can
 * type those getters by referencing element *interfaces* (lazy) instead of
 * re-inferring `typeof MetaModel` (which contains `types.map(Dependency)` etc.
 * and would loop back into each model's own initializer). All members below have
 * the same shape and return types as the MST views/actions defined further down.
 */
export interface IMetaModel {
  readonly objectTypes: IObjectType[];
  readonly eventTypes: IEventType[];
  readonly dependencies: IDependency[];
  readonly methods: IMethod[];
  readonly inheritances: IInheritance[];
  readonly dataTypes: IDataType[];
  readonly eventNames: string[];
  readonly acquiredMethods: IMethod[];
  readonly ownedMethods: IMethod[];
  readonly inheritedMethods: IMethod[];
  readonly specialisedMethods: IMethod[];
  readonly availableDataTypes: string[];
  readonly multiplePropagationConstraints: IMultiplePropagationConstraint[];
  readonly fsms: IFSM[];
  readonly states: IState[];
  readonly transitions: ITransition[];
  findById(id: string): IObjectType | IEventType | IDependency | IMethod | IInheritance | IDataType | IFSM | IState | ITransition | undefined;
  getEventByName(name: string): IEventType | undefined;
  getObjectTypeById(id: string): IObjectType | undefined;
  getEventTypeById(id: string): IEventType | undefined;
  getDependencyById(id: string): IDependency | undefined;
  getMethodById(id: string): IMethod | undefined;
  getInheritanceById(id: string): IInheritance | undefined;
  getDataTypeById(id: string): IDataType | undefined;
  isBuiltinDataType(name: string): boolean;
  getDataTypeByName(name: string): IDataType | undefined;
  getFSMById(id: string): IFSM | undefined;
  getStateById(id: string): IState | undefined;
  getTransitionById(id: string): ITransition | undefined;
  addObjectType(id: string, name: string, description: string, abstract?: boolean): IObjectType;
  addEventType(id: string, name: string, ownerEventType?: string, inheritance?: string): IEventType;
  addMethod(
    id: string,
    name: string,
    ownerObjectType: string,
    ownerEventType: string,
    provenance: 'OWNED' | 'ACQUIRED' | 'INHERITED' | 'SPECIALISED',
    type: 'CREATE' | 'MODIFY' | 'END',
    viaDependency?: string | null,
    viaMethod?: string | null,
    viaInheritance?: string | null
  ): IMethod;
  addDependency(
    id: string,
    name: { master: string; dependent: string },
    master: string,
    dependent: string,
    type: 'OPTIONAL_N' | 'OPTIONAL_1' | 'MANDATORY_N' | 'MANDATORY_1'
  ): IDependency;
  addInheritance(id: string, supertype: string, subtype: string): IInheritance;
  addDataType(id: string, name: string): IDataType;
  removeObjectType(id: string): void;
  removeEventType(id: string): void;
  removeDependency(id: string): void;
  removeInheritance(id: string): void;
  removeMethod(id: string): void;
  removeDataType(id: string): void;
}

// A snapshot element carries an id (the MERODE element ids) — the pre/post
// processors index the historical array form by it. Each element snapshot is the
// real MST creation snapshot of the corresponding model, so the produced draft is
// assignable to the MetaModel creation type with no casts.
type ObjectTypeSnap = SnapshotIn<typeof ObjectType>;
type EventTypeSnap = SnapshotIn<typeof EventType>;
type DependencySnap = SnapshotIn<typeof Dependency>;
type MethodSnap = SnapshotIn<typeof Method>;
type InheritanceSnap = SnapshotIn<typeof Inheritance>;
type DataTypeSnap = SnapshotIn<typeof DataType>;

// The mutable snapshot the immer producers see. The model serializes its
// element collections as arrays (the historical/MXP form); MST hydrates them
// into maps keyed by id and back. Both shapes are present across the two hooks.
interface MetaModelSnapshotDraft {
  objectTypes?: ObjectTypeSnap[];
  eventTypes?: EventTypeSnap[];
  dependencies?: DependencySnap[];
  methods?: MethodSnap[];
  inheritances?: InheritanceSnap[];
  dataTypes?: DataTypeSnap[];
  _objectTypes?: Record<string, ObjectTypeSnap>;
  _eventTypes?: Record<string, EventTypeSnap>;
  _dependencies?: Record<string, DependencySnap>;
  _methods?: Record<string, MethodSnap>;
  _inheritances?: Record<string, InheritanceSnap>;
  _dataTypes?: Record<string, DataTypeSnap>;
}

/**
 * A MERODE metamodel.
 */
// The historical default factory `() => {}` is an arrow with a *block* body, so
// it returns `undefined` — MST then falls back to the map's own empty default.
// We must keep it byte-identical, so we cast it to the factory type MST expects
// instead of changing it to `() => ({})` (which would alter the returned value).
const emptyMapDefault = (() => {}) as unknown as () => Record<string, never>;

const MetaModel = types
  .model('MetaModel', {
    // the list of object types (see ./object_type.js)
    _objectTypes: types.optional(types.map(ObjectType), emptyMapDefault),
    // the list of event types (see ./event_type.js)
    _eventTypes: types.optional(types.map(EventType), emptyMapDefault),
    // the list of dependencies (see ./dependency.js)
    _dependencies: types.optional(types.map(Dependency), emptyMapDefault),
    // the list of methods (see ./method.js)
    _methods: types.optional(types.map(Method), emptyMapDefault),
    // the list of inheritances (see ./inheritance.js)
    _inheritances: types.optional(types.map(Inheritance), emptyMapDefault),
    // the list of data types (see ./data_type.js)
    _dataTypes: types.optional(types.map(DataType), emptyMapDefault),
  })
  .preProcessSnapshot(
    // The preProcessSnapshot hook should be a pure function that returns a new
    // snapshot. We use the produce utility from the immer library to do that
    // easily. See https://github.com/mweststrate/immer.
    produce((draft: Draft<MetaModelSnapshotDraft>) => {
      // Historically, the lists of elements were arrays. This is conceptually easier, but comes
      // with a huge performance penalty for getting an element by id. Since our reference resolving
      // depends on getting an element by id, performance was suffering too much for large models.
      // To optimize this while keeping our JSON format identical, we now pre/post process the
      // snapshots to convert from/to maps iso arrays.
      // `as unknown` keeps the guard from narrowing `draft` to `never` (immer's
      // WritableDraft is otherwise treated as assignable to IAnyStateTreeNode).
      if (!isStateTreeNode(draft as unknown)) {
        draft._objectTypes = {};
        for (const o of draft.objectTypes || []) {
          draft._objectTypes[o.id] = o;
        }
        draft._eventTypes = {};
        for (const e of draft.eventTypes || []) {
          draft._eventTypes[e.id] = e;
        }
        draft._dependencies = {};
        for (const d of draft.dependencies || []) {
          draft._dependencies[d.id] = d;
        }
        draft._methods = {};
        for (const m of draft.methods || []) {
          draft._methods[m.id] = m;
        }
        draft._inheritances = {};
        for (const i of draft.inheritances || []) {
          draft._inheritances[i.id] = i;
        }
        draft._dataTypes = {};
        for (const d of draft.dataTypes || []) {
          draft._dataTypes[d.id] = d;
        }
      }
    })
  )
  .postProcessSnapshot(
    produce((draft: Draft<MetaModelSnapshotDraft>) => {
      if (!isStateTreeNode(draft as unknown)) {
        draft.objectTypes = Object.values(draft._objectTypes!);
        delete draft._objectTypes;
        draft.eventTypes = Object.values(draft._eventTypes!);
        delete draft._eventTypes;
        draft.dependencies = Object.values(draft._dependencies!);
        delete draft._dependencies;
        draft.methods = Object.values(draft._methods!);
        delete draft._methods;
        draft.inheritances = Object.values(draft._inheritances!);
        delete draft._inheritances;
        draft.dataTypes = Object.values(draft._dataTypes!);
        delete draft._dataTypes;
      }
    })
  )
  .views((self0) => {
    // MST types `self` without the views being defined in this same block, so
    // the sibling getters/finders below would not see each other. We rebind to a
    // richer view of the very same node (the model props from `self0` plus the
    // hand-declared IMetaModel surface). This is purely a type-level alias — `self`
    // and `self0` are the identical observable node at runtime.
    const self = self0 as typeof self0 & IMetaModel;
    return {
    get objectTypes() {
      return values(self._objectTypes);
    },
    get eventTypes() {
      return values(self._eventTypes);
    },
    get dependencies() {
      return values(self._dependencies);
    },
    get methods() {
      return values(self._methods);
    },
    get inheritances() {
      return values(self._inheritances);
    },
    get dataTypes() {
      return values(self._dataTypes);
    },
    // Find any by type
    findById(id: string) {
      return (
        self.getObjectTypeById(id) ||
        self.getEventTypeById(id) ||
        self.getDependencyById(id) ||
        self.getMethodById(id) ||
        self.getInheritanceById(id) ||
        self.getDataTypeById(id) ||
        self.getFSMById(id) ||
        self.getStateById(id) ||
        self.getTransitionById(id)
      );
    },
    // An array of the names of all event types
    get eventNames() {
      return self.eventTypes.map((et) => et.name);
    },
    getEventByName(name: string) {
      return self.eventTypes.find((et) => et.name === name);
    },
    getObjectTypeById(id: string) {
      return self._objectTypes.get(id);
    },
    getEventTypeById(id: string) {
      return self._eventTypes.get(id);
    },
    getDependencyById(id: string) {
      return self._dependencies.get(id);
    },
    getMethodById(id: string) {
      return self._methods.get(id);
    },
    getInheritanceById(id: string) {
      return self._inheritances.get(id);
    },
    getDataTypeById(id: string) {
      return self._dataTypes.get(id);
    },
    // Is the given data type a built-in? See the list of BUILTIN_DATA_TYPES in
    // ./data_type.js
    isBuiltinDataType(name: string) {
      return BUILTIN_DATA_TYPES.includes(name);
    },
    getDataTypeByName(name: string) {
      return self.dataTypes.find((dt) => dt.name === name);
    },
    // Filtered method list with only the acquired methods
    get acquiredMethods() {
      return self.methods.filter((m) => m.provenance === 'ACQUIRED');
    },
    // Filtered method list with only the owned methods
    get ownedMethods() {
      return self.methods.filter((m) => m.provenance === 'OWNED');
    },
    // Filtered method list with only the inherited methods
    get inheritedMethods() {
      return self.methods.filter((m) => m.provenance === 'INHERITED');
    },
    // Filtered method list with only the specialised methods
    get specialisedMethods() {
      return self.methods.filter((m) => m.provenance === 'SPECIALISED');
    },
    // Concatenation of the builtin data types and the names of the custom data
    // types.
    get availableDataTypes() {
      return BUILTIN_DATA_TYPES.concat(self.dataTypes.map((dt) => dt.name));
    },
    get multiplePropagationConstraints() {
      return flatten(self.objectTypes.map((o) => o.multiplePropagationConstraints.slice()));
    },
    get fsms() {
      return flatten(self.objectTypes.map((o) => o.fsms.slice()));
    },
    get states() {
      return flatten(self.fsms.map((f) => f.states.slice()));
    },
    get transitions() {
      return flatten(self.fsms.map((f) => f.transitions.slice()));
    },
    getFSMById(id: string) {
      return self.fsms.find((f) => f.id === id);
    },
    getStateById(id: string) {
      return self.states.find((s) => s.id === id);
    },
    getTransitionById(id: string) {
      return self.transitions.find((t) => t.id === id);
    },
    };
  })
  .actions((self) => ({
    addObjectType(id: string, name: string, description: string, abstract = false) {
      const objectType = ObjectType.create({ name, description, abstract, id });
      self._objectTypes.put(objectType);
      return objectType;
    },
    addEventType(
      id: string,
      name: string,
      ownerEventType?: string,
      inheritance?: string
    ) {
      const eventType = EventType.create({ name, id, ownerEventType, inheritance });
      self._eventTypes.put(eventType);
      return eventType;
    },
    addMethod(
      id: string,
      name: string,
      ownerObjectType: string,
      ownerEventType: string,
      provenance: 'OWNED' | 'ACQUIRED' | 'INHERITED' | 'SPECIALISED',
      type: 'CREATE' | 'MODIFY' | 'END',
      viaDependency: string | null = null,
      viaMethod: string | null = null,
      viaInheritance: string | null = null
    ) {
      const method = Method.create({
        id,
        name,
        ownerEventType,
        ownerObjectType,
        provenance,
        type,
        viaDependency,
        viaMethod,
        viaInheritance,
      });
      self._methods.put(method);
      return method;
    },
    addDependency(
      id: string,
      name: { master: string; dependent: string },
      master: string,
      dependent: string,
      type: 'OPTIONAL_N' | 'OPTIONAL_1' | 'MANDATORY_N' | 'MANDATORY_1'
    ) {
      const dependency = Dependency.create({
        id,
        name,
        master,
        dependent,
        type,
      });
      self._dependencies.put(dependency);
      return dependency;
    },
    addInheritance(id: string, supertype: string, subtype: string) {
      const inheritance = Inheritance.create({
        id,
        supertype,
        subtype,
      });
      self._inheritances.put(inheritance);
      return inheritance;
    },
    addDataType(id: string, name: string) {
      const dataType = DataType.create({
        id,
        name,
      });
      self._dataTypes.put(dataType);
      return dataType;
    },
    // id? To be able to replay, we need to have serializable arguments!
    removeObjectType(id: string) {
      self._objectTypes.delete(id);
    },
    // id? To be able to replay, we need to have serializable arguments!
    removeEventType(id: string) {
      self._eventTypes.delete(id);
    },
    // id? To be able to replay, we need to have serializable arguments!
    removeDependency(id: string) {
      self._dependencies.delete(id);
    },
    // id? To be able to replay, we need to have serializable arguments
    removeInheritance(id: string) {
      self._inheritances.delete(id);
    },
    // id? To be able to replay, we need to have serializable arguments!
    removeMethod(id: string) {
      self._methods.delete(id);
    },
    // id? To be able to replay, we need to have serializable arguments!
    removeDataType(id: string) {
      self._dataTypes.delete(id);
    },
  }));

export default MetaModel;
